import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Alert from "@/components/ui/alert/Alert";
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "@/service/api";
import DatePicker from "@/components/Usados/date-picker";

declare global {
  interface Window {
    AuraSurveyCreator: any;
  }
}

type FormState = {
  nombre: string;
  descripcion: string;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  ficha_tecnica: string;
  codigo: string;
  tipo_estudio: string;
  fuente_financiacion: string;
  poblacion_objetivo: string;
  cobertura_geografica: string;
  cantidad: number;
  margen_error: string;
  metodo_recoleccion: string;
  documento: string;
};

const INITIAL_FORM: FormState = {
  nombre: "",
  descripcion: "",
  fecha_inicio: null,
  fecha_fin: null,
  ficha_tecnica: "",
  codigo: "",
  tipo_estudio: "",
  fuente_financiacion: "",
  poblacion_objetivo: "",
  cobertura_geografica: "",
  cantidad: 0,
  margen_error: "",
  metodo_recoleccion: "",
  documento: "",
};
export default function CreateSurvey() {
  const creatorRef = useRef<HTMLDivElement>(null);
  const creatorInstanceRef = useRef<any>(null);
  const formDataRef = useRef(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<FormState>(INITIAL_FORM);
  const [openOpcionales, setOpenOpcionales] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [documentName, setDocumentName] = useState<string | null>(null);
  
  const handleOpenDocument = async () => {
    try {
      if (!formData.documento) return;

      const filename = formData.documento.split("/").pop();
      if (!filename) return;
      const response = await apiFetch(`files/survey-document/${filename}`, {
        method: "GET",
      });

      if (!response.ok) throw new Error("Failed to fetch file");

      const blob = await response.blob();
      const fileURL = URL.createObjectURL(blob);
      const extension = filename.split(".").pop()?.toLowerCase();

      if (extension === "pdf") {
        const printWindow = window.open(fileURL, "_blank");
        if (!printWindow) {
          window.open(fileURL, "_blank");
          return;
        }
      } else {
        const a = document.createElement("a");
        a.href = fileURL;
        a.download = documentName || filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
      }
    } catch (err) {
      console.error("Error al obtener el documento", err);
      setError("No se pudo abrir el documento");
    }
  };

  const handleDocumentChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);

      const res: any = await apiFetch(`files/upload/survey-document`, {
        method: "POST",
        body: form as any,
      });

      let data = res;
      if (res && typeof res.ok !== "undefined") {
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.message || "Error subiendo documento");
        }
        data = await res.json();
      }

      setFormData((p) => ({ ...p, documento: data.filePath }));
      setDocumentName(data.file);
    } catch (err: any) {
      setError(err.message || "Error al subir el documento");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleRemoveDocument = async () => {
    if (formData.documento) {
      try {
        await apiFetch(`files/delete`, {
          method: "POST",
          body: JSON.stringify({ filePath: formData.documento }),
        });
      } catch {}
    }
    setFormData((p) => ({ ...p, documento: "" }));
    setDocumentName(null);
  };

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: name === "cantidad" ? Number(value) : value,
    }));
  };

  const resetAll = () => {
    setFormData(INITIAL_FORM);
    formDataRef.current = INITIAL_FORM;
    setOpenOpcionales(false);
    setDocumentName(null);

    const creator = creatorInstanceRef.current;
    if (creator) {
      creator.schema = {
        title: "",
        description: "",
        pages: [{ name: "page1", title: "Tu opinion", elements: [] }],
      };
      creator.pageIndex = 0;
      creator.selectedIndex = -1;
      creator.clearDraft?.();
      localStorage.removeItem("aura-demo-draft");
      localStorage.removeItem("aura-survey-draft");
      creator.renderShell?.();
    }
  };

  useEffect(() => {
    const loadCss = (href: string) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      document.head.appendChild(link);
    };
    loadCss("/libs/aura-survey/css/aura-survey.css");
    loadCss("/libs/aura-survey/css/aura-survey-creator.css");

    const load = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(
          `script[src="${src}"]`,
        );
        if (existing) {
          if (existing.dataset.loaded) return resolve();
          existing.addEventListener("load", () => resolve());
          return;
        }
        const script = document.createElement("script");
        script.src = src;
        script.onload = () => {
          script.dataset.loaded = "true";
          resolve();
        };
        script.onerror = () => reject(new Error(`No se encontró ${src}`));
        document.body.appendChild(script);
      });

    let creator: any;

    Promise.all([
      load("/libs/aura-survey/js/aura-survey.js"),
      load("/libs/aura-survey/js/aura-survey-creator.js"),
    ])
      .then(() => {
        if (!creatorRef.current) return;
        localStorage.removeItem("aura-demo-draft");
        localStorage.removeItem("aura-survey-draft");

        creator = new window.AuraSurveyCreator(creatorRef.current, {
          schema: {
            title: formDataRef.current?.nombre || "Nueva encuesta",
            description: formDataRef.current?.descripcion || "",
            pages: [{ name: "page1", title: "Tu opinion", elements: [] }],
          },
          storageKey: undefined,
          onSave: async (finalSchema: any) => {
            if (!formDataRef.current?.nombre?.trim()) {
              setError("El nombre es obligatorio");
              return;
            }
            if (!formDataRef.current?.codigo?.trim()) {
              setError("El código es obligatorio");
              return;
            }

            finalSchema.title = formDataRef.current.nombre;
            finalSchema.description = formDataRef.current.descripcion;

            const payload = {
              survey: finalSchema,
              ...formDataRef.current,
            };

            try {
              setIsSaving(true);
              setError(null);
              setSuccess(false);

              const response: any = await apiFetch(`survey/create`, {
                method: "POST",
                body: JSON.stringify(payload),
              });

              if (
                response &&
                typeof response.ok !== "undefined" &&
                !response.ok
              ) {
                const data = await response.json().catch(() => null);
                throw new Error(data?.message || `Error ${response.status}`);
              }

              setSuccess(true);
              resetAll();
              setTimeout(() => setSuccess(false), 3000);
            } catch (err: any) {
              console.error(err);
              setError(err.message || "Error al guardar la encuesta");
            } finally {
              setIsSaving(false);
            }
          },
        });
        creatorInstanceRef.current = creator;
      })
      .catch((err) => setError(err.message));

    return () => creator?.destroy?.();
  }, []);

  useEffect(() => {
    const creator: any = creatorInstanceRef.current;
    if (!creator || !creator.schema) return;
    creator.schema.title = formData.nombre;
    creator.schema.description = formData.descripcion;
    if (creator.root) {
      const titleInput = creator.root.querySelector(
        '[data-field="schema-title"]',
      ) as HTMLInputElement;
      if (titleInput && document.activeElement !== titleInput) {
        titleInput.value = formData.nombre;
      }
      const h1 = creator.root.querySelector(
        ".creator-canvas-heading h1",
      ) as HTMLElement;
      if (h1) h1.textContent = formData.nombre || "Nueva encuesta";
    }
    creator.persist?.();
    creator.emit?.("change", creator.getSchema());
  }, [formData.nombre, formData.descripcion]);

  const inputClass =
    "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";
  const labelClass =
    "mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400";

  return (
    <div>
      <PageBreadcrumb pageTitle="Crear Encuesta" />
      <div className="rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12 space-y-8">
        <h3 className="font-semibold text-gray-800 text-center text-xl dark:text-white/90">
          Crear Nueva Encuesta
        </h3>
        {error && <Alert variant="error" title="Error" message={error} />}
        {success && (
          <Alert
            variant="success"
            title="Éxito"
            message="Encuesta creada. Puedes crear otra."
          />
        )}

        {/* CAMPOS OBLIGATORIOS */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label className={labelClass}>Nombre *</label>
            <input
              name="nombre"
              value={formData.nombre}
              onChange={handleInputChange}
              className={inputClass}
              placeholder="Ej: Encuesta de satisfacción"
            />
          </div>
          <div>
            <label className={labelClass}>Código *</label>
            <input
              name="codigo"
              value={formData.codigo}
              onChange={handleInputChange}
              className={inputClass}
              placeholder="Ej: ENC-001"
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelClass}>Descripción</label>
            <textarea
              name="descripcion"
              value={formData.descripcion}
              onChange={handleInputChange}
              rows={2}
              className={inputClass + " h-auto"}
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelClass}>
              Documento de la encuesta (PDF, DOCX)
            </label>
            <div className="flex items-center gap-3">
              <label
                className={`flex h-11 cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 px-6 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-white/70 ${
                  uploadingDoc ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleDocumentChange}
                />
                {uploadingDoc
                  ? "Subiendo..."
                  : formData.documento
                  ? "Cambiar documento"
                  : "Seleccionar documento"}
              </label>
              {formData.documento && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                  <button
                    type="button"
                    onClick={handleOpenDocument}
                    className="max-w- truncate text-left text-sm font-medium text-brand-500 underline hover:text-brand-600"
                  >
                    {documentName || formData.documento}
                  </button>
                  <button
                    type="button"
                    onClick={handleRemoveDocument}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    Quitar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DESPLEGABLE - CAMPOS OPCIONALES */}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setOpenOpcionales((v) => !v)}
            className="flex w-full items-center justify-between bg-gray-50 px-5 py-4 text-left transition hover:bg-gray-100 dark:bg-white/[0.03] dark:hover:bg-white/[0.06]"
          >
            <span className="text-sm font-semibold text-gray-800 dark:text-white/90">
              Campos opcionales (adicionales)
            </span>
            <div
              className={
                openOpcionales
                  ? "rotate-180 transition-transform duration-200"
                  : "transition-transform duration-200"
              }
            >
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                className="text-gray-500"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </button>

          {openOpcionales && (
            <div className="grid grid-cols-1 gap-6 border-t border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-transparent md:grid-cols-2 xl:p-6">
              <div>
                <label className={labelClass}>Fecha inicio</label>
                <DatePicker
                  id="fecha_inicio"
                  placeholder="Selecciona una fecha"
                  defaultDate={
                    formData.fecha_inicio
                      ? new Date(formData.fecha_inicio)
                      : undefined
                  }
                  onChange={(selectedDates: Date[]) =>
                    setFormData((p) => ({
                      ...p,
                      fecha_inicio:
                        selectedDates[0]?.toISOString().split("T")[0] || null,
                    }))
                  }
                />
              </div>
              <div>
                <label className={labelClass}>Fecha fin</label>
                <DatePicker
                  id="fecha_fin"
                  placeholder="Selecciona una fecha"
                  defaultDate={
                    formData.fecha_fin
                      ? new Date(formData.fecha_fin)
                      : undefined
                  }
                  onChange={(selectedDates: Date[]) =>
                    setFormData((p) => ({
                      ...p,
                      fecha_fin:
                        selectedDates[0]?.toISOString().split("T")[0] || null,
                    }))
                  }
                />
              </div>
              <div>
                <label className={labelClass}>Tipo estudio</label>
                <input
                  name="tipo_estudio"
                  value={formData.tipo_estudio}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Método recolección</label>
                <input
                  name="metodo_recoleccion"
                  value={formData.metodo_recoleccion}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Fuente financiación</label>
                <input
                  name="fuente_financiacion"
                  value={formData.fuente_financiacion}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Población objetivo</label>
                <input
                  name="poblacion_objetivo"
                  value={formData.poblacion_objetivo}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Cobertura geográfica</label>
                <input
                  name="cobertura_geografica"
                  value={formData.cobertura_geografica}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Ficha técnica</label>
                <input
                  name="ficha_tecnica"
                  value={formData.ficha_tecnica}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Cantidad</label>
                <input
                  type="number"
                  name="cantidad"
                  value={formData.cantidad}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Margen error</label>
                <input
                  name="margen_error"
                  value={formData.margen_error}
                  onChange={handleInputChange}
                  className={inputClass}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className="mb-4 text-lg font-semibold dark:text-white">
            Constructor {isSaving && "(Guardando...)"}
          </h4>
          <div
            ref={creatorRef}
            className="w-full rounded-lg border border-gray-200 dark:border-gray-700"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={resetAll}
            className="h-11 rounded-lg border px-6 text-sm dark:border-gray-700 dark:text-white"
          >
            Limpiar formulario
          </button>
        </div>
      </div>
    </div>
  );
}
